import { createFileRoute } from "@tanstack/react-router";
import Link from "@/components/link";
import { PageHeader } from "@/components/page-header";
import { DateRangeFilter } from "@/components/date-range-filter";
import { SetupPanel } from "@/components/setup-panel";
import { SearchInput } from "@/components/search-input";
import { DataTable, type DataColumn } from "@/components/data-table";
import { ChartCard } from "@/components/charts/chart-card";
import { LiveVisitors } from "@/components/live-visitors";
import { TrackingNote } from "@/components/tracking-note";
import { Sk, SkHeader, SkPanel, SkRows } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import { DEFAULT_PRESET } from "@shared/analytics-range";
import { fmtDayTime, fmtNum } from "@shared/format";
import type { VisitorsPage } from "@shared/pages/visitors";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

export const Route = createFileRoute("/_app/shoppers/visitors")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<VisitorsPage>("visitors", deps));
  },
  component: VisitorsRoute,
});

const COLUMNS: DataColumn[] = [
  { key: "last", label: "Last seen", sortBy: "lastSeen" },
  { key: "first", label: "First seen", sortBy: "firstSeen" },
  { key: "visits", label: "Visits", format: "num", align: "right" },
  { key: "pageViews", label: "Pages", format: "num", align: "right" },
  { key: "productsViewed", label: "Products seen", format: "num", align: "right" },
  { key: "cartAdds", label: "Cart adds", format: "num", align: "right" },
  { key: "bought", label: "Bought", hint: "Orders this browser placed in the range." },
  { key: "customer", label: "Customer", hint: "Tied to a Shopify customer, by an order or a signed-in visit. The journey shows their name and email." },
  { key: "channel", label: "First visit from" },
  { key: "device", label: "Device" },
  { key: "location", label: "Place" },
  { key: "journey", label: "", format: "journey", journey: "visitor" },
];

const cap = (s: string | null) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

// Visitors: header, the live strip, then search and the table.
function Skeleton() {
  return (
    <div className="page-shell">
      <SkHeader />
      <SkPanel bodyClassName="h-24" />
      <Sk className="mt-4 h-9 w-full rounded-lg @3xl:w-96" />
      <div className="mt-4">
        <SkRows count={10} />
      </div>
    </div>
  );
}

function VisitorsRoute() {
  useTitle("Visitors");
  const search = Route.useSearch();
  return (
    <PageQuery<VisitorsPage> name="visitors" search={search} skeleton={<Skeleton />}>
      {(page) => <Visitors page={page} sp={search} />}
    </PageQuery>
  );
}

function Visitors({ page, sp }: { page: VisitorsPage; sp: Record<string, string | undefined> }) {
  const { range: r, status, live, list } = page;
  const header = (
    <PageHeader
      title="Visitors"
      info="Who is on the store now, and every shopper seen in this range."
      actions={<DateRangeFilter presets={r.presets} defaultKey={DEFAULT_PRESET} />}
    />
  );

  if (!live || !list) {
    return (
      <div className="page-shell">
        {header}
        <SetupPanel
          title="Add the pixel in Shopify"
          href={page.canManage ? "/settings#shopper-tracking" : undefined}
          linkLabel="Open the pixel code in Settings"
        >
          Visitors come from the shopper pixel in Shopify admin under Settings, Customer events. Once it is connected, shoppers show here within a
          minute.
        </SetupPanel>
      </div>
    );
  }

  const rows = list.rows.map((v) => ({
    id: v.browserId,
    lastSeen: v.lastSeen,
    firstSeen: v.firstSeen,
    last: fmtDayTime(v.lastSeen),
    first: fmtDayTime(v.firstSeen),
    visits: v.visits,
    pageViews: v.pageViews,
    productsViewed: v.productsViewed,
    cartAdds: v.cartAdds,
    bought: v.orders ? (v.orders === 1 ? "Yes" : `Yes (${v.orders})`) : "No",
    customer: v.hasCustomer ? "Yes" : "–",
    channel: v.firstChannel,
    device: cap(v.device),
    location: v.location,
    journey: v.browserId,
  }));

  const pageHref = (n: number) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(sp)) if (typeof v === "string" && v && k !== "page") p.set(k, v);
    if (n > 1) p.set("page", String(n));
    const qs = p.toString();
    return qs ? `/shoppers/visitors?${qs}` : "/shoppers/visitors";
  };
  const fromN = list.total ? (list.page - 1) * 50 + 1 : 0;
  const toN = Math.min(list.total, list.page * 50);
  const pager = "rounded-lg border border-border px-2.5 py-1 transition-colors hover:text-foreground";

  return (
    <div className="page-shell">
      {header}
      <TrackingNote status={status} />

      <ChartCard title="On the store now" hint="Shoppers with any page view or action in the last five minutes. From the shopper pixel.">
        <LiveVisitors initial={live} />
      </ChartCard>

      <ChartCard
        className="mt-4"
        title="Shoppers in this range"
        hint="One row per browser, latest first. Eastern time. Search by city, product, page, search words, or an order number such as #1234."
      >
        <div className="mb-3 @3xl:w-96">
          <SearchInput placeholder="Search city, product, page or order" />
        </div>
        <DataTable columns={COLUMNS} rows={rows} rowKey="id" defaultSort="last" pageSize={50} emptyText={sp.q ? "No shoppers match that search." : "No shoppers in this range."} />
        {list.pages > 1 && (
          <div className="mt-3 flex items-center justify-between text-xs text-muted">
            <span>
              {fmtNum(fromN)}-{fmtNum(toN)} of {fmtNum(list.total)}
            </span>
            <span className="flex gap-2">
              {list.page > 1 ? (
                <Link href={pageHref(list.page - 1)} className={pager}>
                  ← Newer
                </Link>
              ) : (
                <span className={`${pager} opacity-40`}>← Newer</span>
              )}
              {list.page < list.pages ? (
                <Link href={pageHref(list.page + 1)} className={pager}>
                  Older →
                </Link>
              ) : (
                <span className={`${pager} opacity-40`}>Older →</span>
              )}
            </span>
          </div>
        )}
      </ChartCard>
    </div>
  );
}
